// THE REAL TELEMETRY SINK for this pipeline, replacing `logger.service`.
//
// `logger.service` (lib/logger) routes to `lib/logger/datadog.ts`, which
// despite its name makes no network call at all -- it just reformats the
// payload into a string and calls `console.error` again. The app's real
// error tracking is Datadog RUM (`@datadog/browser-rum`), already
// initialised in production by `useRUMRecording` (app/hooks). This
// mirrors that file's own dynamic-import pattern rather than importing
// the package eagerly, and is safe to call unconditionally: Datadog's
// browser SDKs no-op safely when called before `.init()` (localhost,
// synthetics, or if RUM failed to load), so there is no need to repeat
// `useRUMRecording`'s own environment gating here.
export const reportRumError = async (
  error: unknown,
  context: Record<string, unknown>
): Promise<void> => {
  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    datadogRum.addError(
      error instanceof Error ? error : new Error(String(error)),
      context
    );
  } catch {
    // RUM unavailable (failed to load, blocked, etc.) -- telemetry
    // failing silently must never take the caller down with it.
  }
};

export const reportRumAction = async (
  name: string,
  context: Record<string, unknown>
): Promise<void> => {
  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    datadogRum.addAction(name, context);
  } catch {
    // Swallow -- see reportRumError.
  }
};

// A handled failure: logged as a RUM action rather than an error, so it
// stays out of Error Tracking. Filter with `@action.name:webgpu_*` and
// `@context.level:warning`.
export const reportRumWarning = (
  name: string,
  context: Record<string, unknown> = {}
): Promise<void> => reportRumAction(name, { level: 'warning', ...context });
